import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { PencilIcon } from "@heroicons/react/24/outline";

import { useAppContext } from "@context/AppContext";
import Alert from "@components/shared/Alert";
import DetailList from "./DetailList";
import EditSubscriberDialog from "./EditSubscriberDialog";
import ExportMenu from "./ExportMenu";
import { DetailRows, exportDetails } from "./exportDetails";
import { smallButton } from "./smallButton";

/** "Street, Town REGION POSTCODE, Country", skipping blank parts. */
const formatAddress = (address?: Record<string, string>) =>
  address &&
  [
    address.street,
    [address.town, address.region, address.postcode].filter(Boolean).join(" "),
    address.country,
  ]
    .filter(Boolean)
    .join(", ");

/** Primary users edit these details; other users see them read only. */
const SubscriberDetails: FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    loggedInInfo: { userDetails },
  } = useAppContext();
  const subscriber = userDetails?.subscriber;
  const rows: DetailRows = [
    [t("subscriber.name"), subscriber?.name],
    [t("subscriber.account_number"), subscriber?.accountNumber],
    [t("subscriber.abn"), subscriber?.abn],
    [t("subscriber.acn"), subscriber?.acn],
    [t("common.address"), formatAddress(subscriber?.address)],
    [t("zenith.profile.home_scheme"), subscriber?.level1WRS?.name],
  ];
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <div className="max-w-xl">
      {saved ? (
        <Alert type="success" className="mb-4">
          {t("profile.subscriber.saved_success")}
        </Alert>
      ) : null}
      <DetailList rows={rows} />
      <div className="mt-4 flex flex-wrap gap-2">
        {userDetails?.isPrimary ? (
          <button
            type="button"
            className={`btn-outline-primary ${smallButton}`}
            onClick={() => {
              setSaved(false);
              setIsEditing(true);
            }}
          >
            <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("common.edit")}
          </button>
        ) : null}
        <ExportMenu
          onExport={format =>
            exportDetails({
              title: subscriber?.name,
              rows,
              fileName: subscriber?.name,
              format,
            })
          }
        />
      </div>
      {isEditing ? (
        <EditSubscriberDialog
          onClose={() => setIsEditing(false)}
          onSaved={() => {
            setIsEditing(false);
            setSaved(true);
          }}
        />
      ) : null}
    </div>
  );
};

export default SubscriberDetails;
